import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Moon, Sun, Monitor, ChevronDown } from 'lucide-react'
import { useTheme } from '../contexts/ThemeContext'
import { cn } from '../lib/utils'

const themeOptions = [
  {
    value: 'system' as const,
    label: 'System',
    icon: Monitor,
    description: 'Follow system preference'
  },
  {
    value: 'light' as const,
    label: 'Light',
    icon: Sun,
    description: 'Light theme'
  },
  {
    value: 'dark' as const,
    label: 'Dark',
    icon: Moon,
    description: 'Dark theme'
  }
]

export function ThemeToggle() {
  const { theme, setTheme, resolvedTheme } = useTheme()
  const [isOpen, setIsOpen] = useState(false)

  const currentOption = themeOptions.find(option => option.value === theme)
  const CurrentIcon = currentOption?.icon || Monitor

  const handleThemeChange = (newTheme: 'system' | 'light' | 'dark') => {
    setTheme(newTheme)
    setIsOpen(false)
  }

  return (
    <div className="relative">
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "flex items-center space-x-2 p-2 rounded-xl border transition-all duration-200",
          "hover:bg-muted/20 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-primary/20",
          resolvedTheme === 'dark' 
            ? "bg-card/50 border-border/30 text-foreground" 
            : "bg-white/80 border-gray-200/50 text-gray-900"
        )}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <div className={cn(
          "p-1.5 rounded-lg",
          resolvedTheme === 'dark' ? "bg-primary/20" : "bg-blue-100"
        )}>
          <CurrentIcon className="h-4 w-4" />
        </div>
        <span className="text-sm font-medium hidden sm:block">
          {currentOption?.label}
        </span>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <ChevronDown className="h-4 w-4" />
        </motion.div>
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className={cn(
              "absolute top-full mt-2 right-0 z-50 min-w-[200px] rounded-xl border shadow-xl",
              "backdrop-blur-xl",
              resolvedTheme === 'dark'
                ? "bg-card/90 border-border/30"
                : "bg-white/90 border-gray-200/50"
            )}
          >
            <div className="p-2 space-y-1">
              {themeOptions.map((option) => {
                const Icon = option.icon
                const isActive = theme === option.value
                
                return (
                  <motion.button
                    key={option.value}
                    onClick={() => handleThemeChange(option.value)}
                    className={cn(
                      "w-full flex items-center space-x-3 p-3 rounded-lg text-left transition-all duration-200",
                      "hover:bg-muted/20 focus:outline-none focus:ring-2 focus:ring-primary/20",
                      isActive && "bg-primary/10 border border-primary/20"
                    )}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className={cn(
                      "p-1.5 rounded-lg",
                      isActive 
                        ? "bg-primary/20 text-primary" 
                        : resolvedTheme === 'dark' 
                          ? "bg-muted/50 text-muted-foreground" 
                          : "bg-gray-100 text-gray-600"
                    )}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-medium">
                        {option.label}
                      </div>
                      <div className={cn(
                        "text-xs",
                        resolvedTheme === 'dark' ? "text-muted-foreground" : "text-gray-500"
                      )}>
                        {option.description}
                      </div>
                    </div>
                    {isActive && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="w-2 h-2 bg-primary rounded-full"
                      />
                    )}
                  </motion.button>
                )
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Backdrop */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
        )}
      </AnimatePresence>
    </div>
  )
} 